import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import apiConfig from "../utils/apiConfig";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";
import {
  IListingOptimisation,
  IStageListingOptimisationItem,
} from "../models/listingOptimisation/IListingOptimisation";
import { IBulkRelistResponse } from "./useBatchListingsMutations";

export default function useListingOptimisation(componentName = "useListingOptimisation") {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const pendingOptimisations = useQuery({
    queryKey: ["listingOptimisations"],
    queryFn: () =>
      get<DefaultResponseWithData<IListingOptimisation[]>>("/listingoptimisation"),
  });

  const stageChanges = useMutation(
    (items: IStageListingOptimisationItem[]) =>
      post<any, DefaultResponseWithData<IListingOptimisation[]>>(
        "/listingoptimisation/stage",
        { items }
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["listingOptimisations"]);
      },
    }
  );

  const discardChanges = useMutation(
    (partDataIds: string[]) =>
      post<any, DefaultResponse>("/listingoptimisation/discard", { partDataIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["listingOptimisations"]);
      },
    }
  );

  const sendToRelist = useMutation(
    (body: { partDataIds: string[]; appendSku: boolean }) =>
      post<any, IBulkRelistResponse>("/listingoptimisation/relist", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["listingOptimisations"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  return {
    pendingOptimisations,
    stageChanges,
    discardChanges,
    sendToRelist,
  };
}
